import Enumerable from "linq";
import {
  IFormatBean,
  IFormatFolderBean,
  IFormatFolderRootBean,
  IFormatStatusBean,
  IUserDefinitionBean,
  IUserDefinitionHeaderBean,
} from "./beans";
import {
  FormatReference, FormatSet, Company, FormatStatus, 
} from "../../../domain/format";
import { ReferenceFileInformation } from "../../../domain/document";
import {
  Directory, RootDirectory
} from "../../../domain/drawing";
import { FormatPreviewSource } from "./FormatPreviewSource";
import { FormatThumbnailSource } from "./FormatThumbnailSource";
import { Table, Header, Row, Cell, LightTable, LightRow } from "../../../domain/system/table";
import { IReferenceFileInformationBean } from "../../document/ver2/beans";

export class ObjectTranslator {
  toFormatsTree(getToken: () => string, bean: IFormatFolderRootBean): Directory<string, FormatReference>[] {
    if (!bean) { return []; }
    return this.toFormatsDirectories(getToken, bean.folders);
  }

  private toFormatsDirectories(getToken: () => string, beans: IFormatFolderBean[]): Directory<string, FormatReference>[] {
    return Enumerable.from(beans)
      .select((b, i) => this.toFormatsDirectory(getToken, b, i))
      .toArray();
  }

  private toFormatsDirectory(getToken: () => string, bean: IFormatFolderBean, index: number): Directory<string, FormatReference> {
    const directory = new Directory(
      bean.id,
      bean.name,
      this.toFormatsDirectories(getToken, bean.folders),
      this.toFormats(getToken, bean.formats),
      null,
      bean.recordId
    );
    // Vueのリアクティブに登録すると、プロパティがリアクティブなgetter/setterに置換されてメモリを大量に使用してしまうため、
    // 読み取りしかしないオブジェクトについてはObject.freezeすることでリアクティブにさせないようにしている
    Object.freeze(directory);
    return directory;
  }

  public toFormats(getToken: () => string, beans: IFormatBean[]): FormatReference[] {
    return Enumerable.from(beans)
      .select(b => this.toFormat(getToken, b))
      .toArray();
  }

  public toFormat(getToken: () => string, bean: IFormatBean): FormatReference {
    // 手入力の名前は入れたくない（フォーマット由来の名前を入れたい）が、現状では判定不能
    // バックエンドにツリー編集後の値を返却するときに、手入力された名前かどうかの判定に使用している
    const originalName = bean.name;

    const formatReference = new FormatReference(
      bean.id,
      bean.name,
      new FormatPreviewSource(getToken, bean.id),
      new FormatThumbnailSource(getToken, bean.id),
      bean.reportTypeId,
      bean.masterCode,
      bean.tag,
      new Company(bean.companyCode, bean.companyName),
      bean.categoryType,
      bean.writeMethodType,
      // startDateTime/endDateTimeはMomentで変換してたが、6700個×2プロパティの変換に4秒程度かかってる...使ってないようなので一旦文字列保持に変更.いるかどうかから再考.
      bean.startDateTime ? bean.startDateTime : null,
      bean.endDateTime ? bean.endDateTime : null,
      bean.expired,
      bean.deleted,
      bean.editedName,
      originalName,
      bean.note,
      bean.quoteBehavior,
    );
    // Vueのリアクティブに登録すると、プロパティがリアクティブなgetter/setterに置換されてメモリを大量に使用してしまうため、
    // 読み取りしかしないオブジェクトについてはObject.freezeすることでリアクティブにさせないようにしている
    Object.freeze(formatReference);
    return formatReference;
  }

  toFormatSetTree(getToken: () => string, bean: IFormatFolderRootBean): RootDirectory<string, FormatSet> {
    const folderBeans = (!bean || !bean.folders)
      ? []
      : bean.folders;
    const rootFolderBean: IFormatFolderBean = {
      id: null,
      name: null,
      folders: folderBeans,
      formats: [],
      isFormatSet: false,
      recordId: null,
    };
    const directory = this.toFormatSetDirectory(getToken, rootFolderBean);
    return new RootDirectory(directory.nodes, directory.entities);
  }

  private toFormatSetDirectory(getToken: () => string, bean: IFormatFolderBean, shouldClearId: boolean = false): Directory<string, FormatSet> {
    const directories: Directory<string, FormatSet>[] = [];
    const formatSets: FormatSet[] = [];
    for (const folder of bean.folders) {
      if (folder.folders && folder.folders.length !== 0) {
        // 配下にフォルダとフォーマットが混在しているとフォルダと文書セットでid重複してしまうのでフォルダのidをクリアする対応している
        // V1では混在している構造のツリーを作成できてしまう
        const isMixedDirectory = (folder.formats && folder.formats.length !== 0);
        directories.push(this.toFormatSetDirectory(getToken, folder, isMixedDirectory));
      }
      // フォルダの中のフォーマットはひとつのセットとみなしている
      if (folder.formats && folder.formats.length !== 0) {
        formatSets.push(this.toFormatSet(getToken, folder.id, folder.name, folder.formats, folder.recordId));
      }
      // フォルダ内にフォーマットもフォルダも無い場合はフラグで判定する
      if (folder.folders && folder.folders.length === 0 && folder.formats && folder.formats.length === 0) {
        if (folder.isFormatSet) {
          formatSets.push(this.toFormatSet(getToken, folder.id, folder.name, folder.formats, folder.recordId));
        } else {
          directories.push(this.toFormatSetDirectory(getToken, folder));
        }
      }
    }

    return new Directory(
      shouldClearId ? "" : bean.id,
      bean.name,
      directories,
      formatSets,
      null,
      shouldClearId ? null : bean.recordId
    );
  }

  private toFormatSet(getToken: () => string, id: string, name: string, beans: IFormatBean[], recordId: number): FormatSet {
    const formats: FormatReference[] = [];
    for (const bean of beans) {
      formats.push(this.toFormat(getToken, bean));
    }
    return new FormatSet(id, name, formats, recordId);
  }

  public toUserDefinition(bean: IUserDefinitionBean): Table {
    const rows: Row[] = [];
    for (const rowBean of bean.rows) {
      const cells: Cell[] = [];
      for (const value of rowBean.values) {
        cells.push(new Cell(value));
      }
      rows.push(new Row(cells));
    }
    const headers = bean.headers.map(h => this.toHeader(h));
    return new Table(headers, rows);
  }

  toUserDefinitionAsLightTable(bean: IUserDefinitionBean): LightTable {
    const rows: LightRow[] = [];
    for (const rowBean of bean.rows) {
      // TODO: concatする？
      const values = !!rowBean.values ? rowBean.values : [];
      const row = new LightRow(values);
      rows.push(row);
    }
    const headers = bean.headers.map(h => this.toHeader(h));
    return new LightTable(headers, rows);
  }

  private toHeader(bean: IUserDefinitionHeaderBean): Header {
    const title = bean.title;
    const width = (bean.width != null) ? bean.width * 1.3 : null;
    return new Header(title, title, width, bean.isVisible);
  }

  toFormatFolderRootBean(folders: Directory<string, FormatReference>[]): IFormatFolderRootBean {
    return {
      folders: this.toFormatFoldersBean(folders),
    };
  }

  private toFormatFoldersBean(folders: Directory<string, FormatReference>[]): IFormatFolderBean[] {
    return Enumerable.from(folders)
      .select(f => this.toFormatFolderBean(f))
      .toArray();
  }

  private toFormatFolderBean(folder: Directory<string, FormatReference>): IFormatFolderBean {
    return {
      id: folder.key,
      name: folder.name,
      folders: this.toFormatFoldersBean(folder.nodes),
      formats: this.toFormatsBean(folder.entities),
      isFormatSet: false,
      recordId: folder.recordId,
    };
  }

  private toFormatsBean(formats: FormatReference[]): IFormatBean[] {
    return Enumerable.from(formats)
      .select(f => this.toFormatBean(f))
      .toArray();
  }

  private toFormatBean(format: FormatReference): IFormatBean {
    // フォーマット由来の名前と手入力の名前を区別したいが、
    // 現状でももろもろUIの整備が追い付いていないので、取得時と返却時にnameが異なっていたら手入力されたと判定している。
    // 名前を変更→保存→元の名前に変更したとき、元の名前がeditedNameに格納されるケースがあり得る
    const editedName = (!!format.originalName && format.name !== format.originalName)
      ? format.name
      : format.editedName;
    
    return {
      id: format.id,
      name: format.name,
      editedName: editedName,
      reportTypeId: format.reportTypeId,
      masterCode: format.masterCode,
      tag: format.tag,
      companyCode: format.company ? format.company.id : null,
      companyName: format.company ? format.company.name : null,
      categoryType: format.categoryType,
      writeMethodType: format.writeMethodType,
      // startDateTime/endDateTimeはMomentで変換してたが、6700個×2プロパティの変換に4秒程度かかってる...使ってないようなので一旦文字列保持に変更.いるかどうかから再考.
      startDateTime: format.startDateTime ? format.startDateTime : "",
      endDateTime: format.endDateTime ? format.endDateTime : "",
      expired: format.expired,
      deleted: format.deleted,
      note: null,
      quoteBehavior: format.quoteBehaviorKind,
    };
  }

  toFormatStatus(bean: IFormatStatusBean): FormatStatus {
    if (!bean) { return null; }
    return new FormatStatus(bean.originalId, bean.id, bean.name, bean.expired, bean.deleted);
  }

  toReferenceFilesInformation(beans: IReferenceFileInformationBean[]): ReferenceFileInformation[] {
    if (!beans) { return null; }
    return Enumerable.from(beans).select(b => this.toReferenceFileInformation(b)).toArray();
  }

  toReferenceFileInformation(bean: IReferenceFileInformationBean): ReferenceFileInformation {
    return new ReferenceFileInformation(
      bean.id,
      bean.name,
    );
  }

  toFormatFolderRootBeanFromFormatSetDirectory(root: RootDirectory<string, FormatSet>): IFormatFolderRootBean {
    const directory = new Directory(null, null, root.nodes, root.entities);
    // FormatSetはIFormatFolderBeanとして表現される
    const folderBean = this.toFormatFolderBeanFromFormatSetDirectory(directory);
    return {
      folders: folderBean.folders,
    };
  }

  private toFormatFolderBeanFromFormatSetDirectory(folder: Directory<string, FormatSet>): IFormatFolderBean {
    return {
      id: folder.key,
      name: folder.name,
      folders: this.toFormatFolderBeansFromFormatSetDirectory(folder.nodes).concat(this.toFormatFolderBeanFromFormatSets(folder.entities)),
      formats: [],
      isFormatSet: false,
      recordId: folder.recordId,
    };
  }

  private toFormatFolderBeansFromFormatSetDirectory(folders: Directory<string, FormatSet>[]): IFormatFolderBean[] {
    return Enumerable.from(folders)
      .select(f => this.toFormatFolderBeanFromFormatSetDirectory(f))
      .toArray();
  }

  private toFormatFolderBeanFromFormatSets(formatSets: FormatSet[]): IFormatFolderBean[] {
    return formatSets.map(s => this.toFormatFolderBeanFromFormatSet(s));
  }

  private toFormatFolderBeanFromFormatSet(formatSet: FormatSet): IFormatFolderBean {
    return {
      id: formatSet.id,
      name: formatSet.name,
      folders: [],
      formats: formatSet.formats.map(f => this.toFormatBean(f)),
      isFormatSet: true,
      recordId: formatSet.recordId,
    };
  }
}
